import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Gift, Flame, Target, Award, Zap, Bot } from "lucide-react";
import { useClicker, CoinBar, fmtCoins, blip } from "./ClickerLayout";

const SKIN_STYLES = {
  classic: "from-[#00F0FF] to-[#0066FF]",
  neon: "from-[#39FF14] to-[#00F0FF]",
  plasma: "from-[#FF2BD6] to-[#7B2BFF]",
  galaxy: "from-[#7B2BFF] via-[#FF2BD6] to-[#00F0FF]",
  gold: "from-[#FFE300] to-[#FF8A00]",
  jixxeo: "from-[#00F0FF] via-[#FFE300] to-[#FF2BD6]",
};

function TapButton() {
  const { state, tap, muted } = useClicker();
  const [pops, setPops] = useState([]);
  const id = useRef(0);
  const boost = state.booster && new Date(state.booster.until) > new Date() ? state.booster.mult : 1;
  const onTap = (e) => {
    tap();
    blip(muted, 520 + Math.random() * 260);
    const r = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX || r.left + r.width / 2) - r.left; const y = (e.clientY || r.top + r.height / 2) - r.top;
    const pid = ++id.current;
    setPops((p) => [...p.slice(-12), { id: pid, x, y, v: state.click_value * boost }]);
    setTimeout(() => setPops((p) => p.filter((q) => q.id !== pid)), 800);
  };
  return (
    <div className="relative flex justify-center py-6 select-none">
      <motion.button whileTap={{ scale: 0.9 }} onPointerDown={onTap} data-testid="clicker-tap-btn"
        className={`relative w-60 h-60 sm:w-72 sm:h-72 rounded-full bg-gradient-to-br ${SKIN_STYLES[state.skin] || SKIN_STYLES.classic} shadow-[0_0_80px_rgba(255,43,214,0.45)] ring-8 ring-white/10 touch-manipulation`}
        style={{ WebkitTapHighlightColor: "transparent" }}>
        <span className="absolute inset-6 rounded-full bg-black/25 backdrop-blur-sm flex flex-col items-center justify-center">
          <span className="font-black text-5xl tracking-tighter">TAP</span>
          <span className="text-xs uppercase tracking-widest text-white/70 mt-1">+{fmtCoins(state.click_value * boost)} each</span>
        </span>
        {boost > 1 && <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-[#FFE300] text-black font-black text-xs px-3 py-1 rounded-full" data-testid="clicker-boost-active">{boost}x BOOST</span>}
        <AnimatePresence>
          {pops.map((p) => (
            <motion.span key={p.id} initial={{ opacity: 1, y: 0 }} animate={{ opacity: 0, y: -70 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.8 }} className="absolute pointer-events-none font-black text-xl text-[#FFE300] drop-shadow"
              style={{ left: p.x - 12, top: p.y - 20 }}>+{fmtCoins(p.v)}</motion.span>
          ))}
        </AnimatePresence>
      </motion.button>
    </div>
  );
}

function LevelBar() {
  const { state } = useClicker();
  const prev = 40 * (state.level - 1) ** 2;
  const pct = Math.min(100, ((state.xp - prev) / Math.max(1, state.next_level_xp - prev)) * 100);
  return (
    <div data-testid="clicker-level">
      <div className="flex justify-between text-[11px] text-white/60 mb-1">
        <span className="font-black text-white">Level {state.level}</span>
        <span>{Math.floor(state.xp)} / {state.next_level_xp} XP</span>
      </div>
      <div className="h-2 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-gradient-to-r from-[#00F0FF] to-[#FF2BD6] transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

function Daily() {
  const { state, action, muted } = useClicker();
  const claimed = state.last_daily === new Date().toISOString().slice(0, 10);
  const claim = async () => {
    const d = await action("/clicker/daily");
    if (d) { blip(muted, 880, 0.2, "sine"); toast.success(`Daily reward: +${fmtCoins(d.reward)} coins`); }
  };
  return (
    <div className="flex items-center gap-3 rounded-2xl bg-gradient-to-r from-[#FF2BD6]/20 to-[#00F0FF]/10 border border-[#FF2BD6]/30 p-3">
      <Gift className="w-8 h-8 text-[#FF2BD6]" />
      <div className="flex-1">
        <div className="font-black text-sm">Daily reward</div>
        <div className="text-[11px] text-white/60 flex items-center gap-1"><Flame className="w-3 h-3 text-[#FF8A00]" /> {state.streak}-day streak</div>
      </div>
      <button disabled={claimed} onClick={claim} data-testid="clicker-daily-btn"
        className="bg-[#FF2BD6] disabled:bg-white/10 disabled:text-white/40 text-black font-black text-xs px-4 py-2 rounded-full transition-colors">
        {claimed ? "Claimed" : "Claim"}
      </button>
    </div>
  );
}

function Missions() {
  const { state, config, action } = useClicker();
  if (!config) return null;
  return (
    <section>
      <h2 className="font-black text-base flex items-center gap-2 mb-2"><Target className="w-4 h-4 text-[#00F0FF]" /> Daily missions</h2>
      <div className="space-y-2">
        {config.missions.map((m) => {
          const prog = Math.min(state.daily?.[m.metric] || 0, m.target);
          const done = prog >= m.target; const claimed = state.daily?.claimed?.includes(m.id);
          return (
            <div key={m.id} className="rounded-xl bg-white/5 border border-white/10 p-3 flex items-center gap-3" data-testid={`clicker-mission-${m.id}`}>
              <div className="flex-1">
                <div className="text-sm font-bold">{m.name}</div>
                <div className="h-1.5 mt-1.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-[#00F0FF]" style={{ width: `${(prog / m.target) * 100}%` }} /></div>
              </div>
              <button disabled={!done || claimed} onClick={async () => { const d = await action(`/clicker/missions/${m.id}/claim`); if (d) toast.success(`+${fmtCoins(d.reward)} coins`); }}
                data-testid={`clicker-mission-claim-${m.id}`}
                className="text-xs font-black px-3 py-1.5 rounded-full bg-[#00F0FF] text-black disabled:bg-white/10 disabled:text-white/40">
                {claimed ? "Done" : `+${fmtCoins(m.reward)}`}
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Achievements() {
  const { state, config } = useClicker();
  if (!config) return null;
  return (
    <section>
      <h2 className="font-black text-base flex items-center gap-2 mb-2"><Award className="w-4 h-4 text-[#FFE300]" /> Achievements · {state.achievements.length}/{config.achievements.length}</h2>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {config.achievements.map((a) => {
          const got = state.achievements.includes(a.id);
          return (
            <div key={a.id} data-testid={`clicker-ach-${a.id}`} className={`rounded-xl p-2 text-center border text-[10px] font-bold ${got ? "border-[#FFE300]/50 bg-[#FFE300]/10 text-[#FFE300]" : "border-white/10 bg-white/5 text-white/30"}`}>
              <Award className="w-5 h-5 mx-auto mb-1" />{a.name}
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default function ClickerPlay() {
  const { state } = useClicker();
  const auto = state.auto && new Date(state.auto.until) > new Date();
  return (
    <div className="space-y-5" data-testid="clicker-play">
      <CoinBar />
      <LevelBar />
      {auto && (
        <div className="flex items-center gap-2 text-xs text-[#39FF14] bg-[#39FF14]/10 border border-[#39FF14]/30 rounded-full px-3 py-1.5 w-fit" data-testid="clicker-auto-running">
          <Bot className="w-3.5 h-3.5" /> Auto clicker · {state.auto.tps} taps/sec
        </div>
      )}
      {state.earn_booster && new Date(state.earn_booster.until) > new Date() && (
        <div className="flex items-center gap-2 text-xs text-[#FFE300]"><Zap className="w-3.5 h-3.5" /> Auto-earn surge {state.earn_booster.mult}x active</div>
      )}
      <TapButton />
      <Daily />
      <Missions />
      <Achievements />
      <p className="text-[10px] text-white/30 text-center">Auto clickers and boosters only work inside Jixxeo Clicker. They can't tap other apps or control your device.</p>
    </div>
  );
}
